import { useCallback } from "react";

import {
	PaymentMethod,
	Reservation_List_Repeater_Type,
	Reservation_List_Reservation_Status,
	Reservation_List_Reservation_Type,
} from "@/lib/gql/__generated__/graphql";

import * as Constants from "../constants";
import { useHandleDate } from "../use-case/use-handle-date";

import type * as Shared from "../../../_shared";
import type * as Repository from "../repository";
import type * as Model from "../type";
import type * as Type from "../type";

type ReservationType = Shared.Model.Type.ViewData.SearchResultTableRowDataOfReservationType;
type QueryData = Repository.Query.QueryData.ReservationListQueryData;
type ViewData = Model.ViewData;

type Props = {
	// チェックイン日での検索
	reservationListByCInDateQueryData: QueryData;
	// チェックアウト日での検索
	reservationListByCOutDateQueryData: QueryData;
	// 宿泊日での検索
	reservationListByStayDateQueryData: QueryData;
	searchModel: Type.SearchModel;
};
type Result = {
	viewData: ViewData;
};

export const useViewDataConverter = () => {
	const { onFormatDateText, onGenerateCalendarDateList } = useHandleDate();

	/**
	 * 予約タイプの変換
	 */
	const getReservationTypeForRows = useCallback(
		(reservationType: Reservation_List_Reservation_Type): ReservationType => {
			if (reservationType === Reservation_List_Reservation_Type.RefundCompleted) {
				return {
					text: "返金対応済",
					labelType: "pink",
				} satisfies ReservationType;
			}

			if (reservationType === Reservation_List_Reservation_Type.DayTrip) {
				return {
					text: "日帰り",
					labelType: "green",
				} satisfies ReservationType;
			}

			if (reservationType === Reservation_List_Reservation_Type.TestReservation) {
				return {
					text: "テスト予約",
					labelType: "green",
				} satisfies ReservationType;
			}

			if (reservationType === Reservation_List_Reservation_Type.HostReservation) {
				return {
					text: "直接予約入力",
					labelType: "pink",
				} satisfies ReservationType;
			}

			return {
				text: "ゲスト予約",
				labelType: "link",
			} satisfies ReservationType;
		},
		[],
	);
	/**
	 * 予約の状態の変換
	 */
	const getReservationStatusTextForRows = useCallback((reservationStatus: Reservation_List_Reservation_Status) => {
		if (reservationStatus === Reservation_List_Reservation_Status.NewRequest) return "新着リクエスト";

		if (reservationStatus === Reservation_List_Reservation_Status.Reserved) return "予約中";

		if (reservationStatus === Reservation_List_Reservation_Status.OnsitePaymentForPaymentError)
			return "決済エラーにより現地徴収";

		if (reservationStatus === Reservation_List_Reservation_Status.CancelledByPaymentError)
			return "決済エラーによるキャンセル";

		if (reservationStatus === Reservation_List_Reservation_Status.Cancelled) return "キャンセル";

		return "-";
	}, []);
	const onConvertSearchResultTableData = useCallback(
		(queryData: QueryData): ViewData["searchResultTableData"]["rowList"] =>
			queryData.reservationList.map(
				({
					hotelID,
					reservationID,
					reservationType,
					reservationStatus,
					guestName,
					guestPostalCode,
					checkIn,
					checkOut,
					reserveDate,
					totalGuestCharge,
					paymentMethod,
					numOfPeople,
					room,
					plan,
					option,
					discount,
					guestTel,
					guestEmail,
					guestAddress,
					isAcceptMailMagazine,
					repeaterType,
				}) => {
					return {
						// ホテルID
						hotelID,
						// 予約ID
						reservationID,
						// 予約番号
						reservationIDText: reservationID,
						// 日帰り予約かどうか
						isDayTrip: reservationType.includes(Reservation_List_Reservation_Type.DayTrip),
						// 予約タイプ
						reservationTypes: reservationType.map((type) => getReservationTypeForRows(type)),
						// 状態
						reservationStatusText: getReservationStatusTextForRows(reservationStatus),
						// 氏名
						guestNameText: guestName,
						// チェックイン
						checkInText: checkIn,
						// チェックアウト
						checkOutText: checkOut,
						// 予約日
						reserveDateText: reserveDate,
						// 合計料金（税込）
						totalGuestChargeText: totalGuestCharge.toLocaleString(),
						// 決済方法
						paymentMethodText: paymentMethod === PaymentMethod.Credit ? "事前決済" : "現地決済",
						// 人数
						numOfPeopleText: `${numOfPeople}名`,
						// 部屋
						roomsText: room.length > 0 ? room.map(({ name }) => name).join(`\n`) : "-",
						// プラン名
						planNameText: plan ?? "-",
						// オプション
						optionText: option.map((option) => `${option.name}${option.amount}個`).join(`\n`),
						// ディスカウント
						discountText: discount ?? "-",
						// 電話番号
						guestTelText: guestTel,
						// メールアドレス
						guestEmailText: guestEmail,
						// 郵便番号
						guestPostalCodeText: guestPostalCode ? `〒${guestPostalCode}` : "",
						// 住所
						guestAddressText: guestAddress,
						// メルマガ配信希望
						isAcceptMailMagazineText: isAcceptMailMagazine ? "⚪︎" : "×",
						// リピータタイプ
						repeaterType: repeaterType ?? Reservation_List_Repeater_Type.NotRepeater,
					};
				},
			),
		[getReservationStatusTextForRows, getReservationTypeForRows],
	);

	/**
	 * チェックイン日での検索結果テーブルデータへ変換
	 */
	const onFilteredQueryDataByCInDate = useCallback((queryData: QueryData, targetDate: string): QueryData => {
		/**
		 * キャンセルされていない & チェックイン日が一致 & 日帰りでない予約
		 */
		const filteredReservationList = queryData.reservationList.filter(
			({ canceledDate, checkIn, reservationType }) =>
				!canceledDate &&
				checkIn === targetDate &&
				!reservationType.includes(Reservation_List_Reservation_Type.DayTrip),
		);
		return {
			...queryData,
			reservationList: filteredReservationList,
		};
	}, []);
	/**
	 * チェックアウト日での検索結果テーブルデータへ変換
	 */
	const onFilteredQueryDataDataByCOutDate = useCallback((queryData: QueryData, targetDate: string): QueryData => {
		/**
		 * キャンセルされていない & チェックアウト日が一致 & 日帰りでない予約
		 */
		const filteredReservationList = queryData.reservationList.filter(
			({ canceledDate, checkOut, reservationType }) =>
				!canceledDate &&
				checkOut === targetDate &&
				!reservationType.includes(Reservation_List_Reservation_Type.DayTrip),
		);
		return {
			...queryData,
			reservationList: filteredReservationList,
		};
	}, []);
	/**
	 * 宿泊日での検索結果テーブルデータへ変換
	 */
	const onFilteredQueryDataByStayDate = useCallback((queryData: QueryData, targetDate: string): QueryData => {
		/**
		 * キャンセルされていない & 宿泊期間が一致 & 日帰りでない予約
		 */
		const filteredReservationList = queryData.reservationList.filter(
			({ canceledDate, checkIn, checkOut, reservationType }) =>
				!canceledDate &&
				checkIn <= targetDate &&
				targetDate < checkOut &&
				!reservationType.includes(Reservation_List_Reservation_Type.DayTrip),
		);
		return {
			...queryData,
			reservationList: filteredReservationList,
		};
	}, []);
	/**
	 * 日帰り（チェックイン日）での検索結果テーブルデータへ変換
	 */
	const onFilteredQueryDataByDayTripCInDate = useCallback((queryData: QueryData, targetDate: string): QueryData => {
		/**
		 * キャンセルされていない & 日帰り（チェックイン日）予約
		 */
		const filteredReservationList = queryData.reservationList.filter(
			({ canceledDate, checkIn, reservationType }) =>
				!canceledDate &&
				reservationType.includes(Reservation_List_Reservation_Type.DayTrip) &&
				checkIn === targetDate,
		);
		return {
			...queryData,
			reservationList: filteredReservationList,
		};
	}, []);

	/**
	 * 予約内容のテーブルデータの生成
	 */
	const onGenerateSearchResultTableData = useCallback(
		({
			reservationListByCInDateQueryData,
			reservationListByCOutDateQueryData,
			reservationListByStayDateQueryData,
			searchModel: {
				searchCondition: { year, month, selectedDate },
				selectedReservationType,
			},
		}: Props): ViewData["searchResultTableData"]["rowList"] => {
			if (selectedDate === undefined) return [];

			let _queryData: QueryData = {
				reservationList: [],
			};

			const selectedDateText = onFormatDateText({ year, month, date: selectedDate });
			/**
			 * 選択された予約タイプによって検索結果を変更
			 */
			if (selectedReservationType === Constants.ReservationTypeForSearchReservationList.cInDate) {
				_queryData = onFilteredQueryDataByCInDate(reservationListByCInDateQueryData, selectedDateText);
			}

			if (selectedReservationType === Constants.ReservationTypeForSearchReservationList.cOutDate) {
				_queryData = onFilteredQueryDataDataByCOutDate(reservationListByCOutDateQueryData, selectedDateText);
			}

			if (selectedReservationType === Constants.ReservationTypeForSearchReservationList.stayDate) {
				_queryData = onFilteredQueryDataByStayDate(reservationListByStayDateQueryData, selectedDateText);
			}

			if (selectedReservationType === Constants.ReservationTypeForSearchReservationList.dayTripCInDate) {
				_queryData = onFilteredQueryDataByDayTripCInDate(reservationListByCInDateQueryData, selectedDateText);
			}

			return onConvertSearchResultTableData(_queryData);
		},
		[
			onConvertSearchResultTableData,
			onFilteredQueryDataByCInDate,
			onFilteredQueryDataByDayTripCInDate,
			onFilteredQueryDataByStayDate,
			onFilteredQueryDataDataByCOutDate,
			onFormatDateText,
		],
	);

	/**
	 * 件数のテキストへ変換
	 */
	const onConvertCountText = useCallback((count: number) => `${count} 件`, []);
	/**
	 * カレンダーに表示する予約の生成
	 */
	const onConvertCalendarReservation = useCallback(
		({
			reservationListByCInDateQueryData,
			reservationListByCOutDateQueryData,
			reservationListByStayDateQueryData,
			reservationListByDayTripCInDate,
			date,
		}: Omit<Props, "searchModel"> & { reservationListByDayTripCInDate: QueryData } & { date: number }) => {
			return {
				date,
				cInDate: {
					reservationType: Constants.ReservationTypeForSearchReservationList.cInDate,
					countText: onConvertCountText(reservationListByCInDateQueryData.reservationList.length),
				},
				cOutDate: {
					reservationType: Constants.ReservationTypeForSearchReservationList.cOutDate,
					countText: onConvertCountText(reservationListByCOutDateQueryData.reservationList.length),
				},
				stayDate: {
					reservationType: Constants.ReservationTypeForSearchReservationList.stayDate,
					countText: onConvertCountText(reservationListByStayDateQueryData.reservationList.length),
				},
				dayTripOfCInDate: {
					reservationType: Constants.ReservationTypeForSearchReservationList.dayTripCInDate,
					countText: onConvertCountText(reservationListByDayTripCInDate.reservationList.length),
				},
			};
		},
		[onConvertCountText],
	);

	/**
	 * カレンダーに表示する予約リストの生成
	 */
	const onGenerateCalendarReservationList = useCallback(
		({
			reservationListByCInDateQueryData,
			reservationListByCOutDateQueryData,
			reservationListByStayDateQueryData,
			searchModel: {
				searchCondition: { year, month },
			},
		}: Props): ViewData["calendarReservationList"] => {
			const calendarDayList = onGenerateCalendarDateList(year, month);

			const calendarReservationList = calendarDayList.map((date) => {
				if (date === undefined) return undefined;

				const formattedDateText = onFormatDateText({ year, month, date });
				/**
				 * 日付でのフィルタリング
				 */
				const filteredQueryDataByCInDate = onFilteredQueryDataByCInDate(
					reservationListByCInDateQueryData,
					formattedDateText,
				);
				const filteredQueryDataByCOutDate = onFilteredQueryDataDataByCOutDate(
					reservationListByCOutDateQueryData,
					formattedDateText,
				);
				const filteredQueryDataByStayDate = onFilteredQueryDataByStayDate(
					reservationListByStayDateQueryData,
					formattedDateText,
				);
				const filteredQueryDataByDayTripCInDate = onFilteredQueryDataByDayTripCInDate(
					reservationListByCInDateQueryData,
					formattedDateText,
				);

				return onConvertCalendarReservation({
					reservationListByCInDateQueryData: filteredQueryDataByCInDate,
					reservationListByCOutDateQueryData: filteredQueryDataByCOutDate,
					reservationListByDayTripCInDate: filteredQueryDataByDayTripCInDate,
					reservationListByStayDateQueryData: filteredQueryDataByStayDate,
					date,
				});
			});

			return calendarReservationList;
		},
		[
			onConvertCalendarReservation,
			onFilteredQueryDataByCInDate,
			onFilteredQueryDataByDayTripCInDate,
			onFilteredQueryDataByStayDate,
			onFilteredQueryDataDataByCOutDate,
			onFormatDateText,
			onGenerateCalendarDateList,
		],
	);

	const onConvertDateText = useCallback(
		({
			searchModel: {
				searchCondition: { year, month, selectedDate },
			},
		}: Pick<Props, "searchModel">) => {
			if (selectedDate === undefined) return "";

			return onFormatDateText({ year, month, date: selectedDate });
		},
		[onFormatDateText],
	);

	const onConvertReservationTypeText = useCallback(
		({ searchModel: { selectedReservationType } }: Pick<Props, "searchModel">) => {
			if (!selectedReservationType) return "";

			let reservationTypeText = "";
			if (selectedReservationType === Constants.ReservationTypeForSearchReservationList.cInDate) {
				reservationTypeText = "チェックイン";
			}
			if (selectedReservationType === Constants.ReservationTypeForSearchReservationList.cOutDate) {
				reservationTypeText = "チェックアウト";
			}
			if (selectedReservationType === Constants.ReservationTypeForSearchReservationList.stayDate) {
				reservationTypeText = "宿泊";
			}
			if (selectedReservationType === Constants.ReservationTypeForSearchReservationList.dayTripCInDate) {
				reservationTypeText = "日帰り（チェックイン）";
			}

			return reservationTypeText + "";
		},
		[],
	);

	const onConvertViewData = useCallback(
		({
			reservationListByCInDateQueryData,
			reservationListByCOutDateQueryData,
			reservationListByStayDateQueryData,
			searchModel,
		}: Props): Result => {
			const calendarReservationList = onGenerateCalendarReservationList({
				reservationListByCInDateQueryData,
				reservationListByCOutDateQueryData,
				reservationListByStayDateQueryData,
				searchModel,
			});

			const dateText = onConvertDateText({ searchModel });
			const reservationTypeText = onConvertReservationTypeText({ searchModel });
			const helpText = dateText + " " + reservationTypeText + "情報";
			const rowList = onGenerateSearchResultTableData({
				reservationListByCInDateQueryData,
				reservationListByCOutDateQueryData,
				reservationListByStayDateQueryData,
				searchModel,
			});

			return {
				viewData: {
					calendarReservationList,
					searchResultTableData: {
						helpText,
						rowList,
					},
				},
			} satisfies Result;
		},
		[
			onConvertDateText,
			onConvertReservationTypeText,
			onGenerateCalendarReservationList,
			onGenerateSearchResultTableData,
		],
	);

	return {
		onConvertViewData,
	};
};
